import { Injectable } from '@angular/core';
import { CategoryTag, CustomProductList, GroupedCategoryTags } from '@catalog/const';
import { CategoryTagsFacade } from '@catalog/data-access';
import { CityService } from '@city/feature';
import { RoutingService } from '@spartacus/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CategoryTagsService {
  // 5 пикселей погрешность при различных разрешениях экрана
  private TAGS_CONTAINER_ONE_LINE_HEIGHT = 40 + 5;
  private DEFAULT_GROUP_NAME = '';

  private _isTagsListOpened: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _tagsListContainerHeight: BehaviorSubject<number> = new BehaviorSubject(0);

  constructor(
    private categoryTagsFacade: CategoryTagsFacade,
    private cityService: CityService,
    private routing: RoutingService,
  ) {}

  /** Получить список всех тегов с группами и без группы */
  getRawTags(): Observable<CategoryTag[]> {
    return combineLatest([this.getActiveBaseStore(), this.getCategoryCode()]).pipe(
      switchMap(([citySlug, categoryCode]) => this.categoryTagsFacade.getCategoryTags(citySlug, categoryCode)),
      filter<CategoryTag[]>(Boolean),
      map((tags) => tags.slice().sort((a, b) => (b.priority || 0) - (a.priority || 0))),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getCategoryTagsLoading(): Observable<boolean> {
    return combineLatest([this.getActiveBaseStore(), this.getCategoryCode()]).pipe(
      switchMap(([citySlug, categoryCode]) => this.categoryTagsFacade.getCategoryTagsLoading(citySlug, categoryCode)),
    );
  }

  /** Получить список тегов без группы */
  getUngroupedTags(): Observable<CategoryTag[]> {
    return this.getRawTags().pipe(map((tags) => tags.filter((t) => !t.group?.name)));
  }

  /** Получить сгруппированный список тегов */
  getGroupedTags(): Observable<GroupedCategoryTags[]> {
    return this.getRawTags().pipe(
      map((tags) => {
        const sortedByGroupObj = tags.reduce<Record<string, CategoryTag[]>>((acc, tag) => {
          const groupName = tag.group?.name || this.DEFAULT_GROUP_NAME;

          if (!acc[groupName]) {
            acc[groupName] = [];
          }

          acc[groupName].push(tag);

          return acc;
        }, {});

        return (
          Object.entries(sortedByGroupObj)
            .map(([groupName, items]) => ({
              groupName,
              items,
            }))
            // теги без группы должны быть вверху списка групп
            .sort((a) => (a.groupName === this.DEFAULT_GROUP_NAME ? -1 : 0))
        );
      }),
    );
  }

  /** Данное свойство меняется при нажатии кнопок Еще/Скрыть */
  getIsTagsListOpened(): Observable<boolean> {
    return this._isTagsListOpened.asObservable();
  }

  /** Установка определенной высоты контейнера с тегами */
  setTagsListContainerHeight(value: number): void {
    this._tagsListContainerHeight.next(value);
  }

  /**
   * Кнопка переключения видимости списка тегов появляется если
   * список тегов не помещается в ${rowsCount} строк. Для этого мы проверяем
   * что высота контейнера больше высоты ${rowsCount} строк.
   *
   * @param rowsCount - количество строк при котором кнопка остается скрытой
   */
  getToggleBtnIsVisible(rowsCount: number): Observable<boolean> {
    return this._tagsListContainerHeight.asObservable().pipe(
      map((height) => {
        return height > this.TAGS_CONTAINER_ONE_LINE_HEIGHT * rowsCount;
      }),
    );
  }

  /** Скрытие/раскрытие списка тегов */
  toggleTagsList(value?: boolean): void {
    value = typeof value === 'boolean' ? value : !this._isTagsListOpened.getValue();

    if (value === this._isTagsListOpened.getValue()) {
      return;
    }

    this._isTagsListOpened.next(value);
  }

  redirectToTagPage(tagSlug: string): void {
    this.routing.go({ cxRoute: 'seoProductList', params: { tagSlug } });
  }

  private getCategoryCode(): Observable<string> {
    return this.routing.getRouterState().pipe(
      switchMap((routerState) => {
        if (routerState.state.semanticRoute === CustomProductList.SeoProductList) {
          return this.categoryTagsFacade.getActiveCategoryTagItem().pipe(
            filter<CategoryTag>(Boolean),
            map((item) => item.categoryCode),
          );
        }

        return of(routerState.state.params.categoryCode);
      }),
      distinctUntilChanged(),
      filter<string>(Boolean),
    );
  }

  private getActiveBaseStore(): Observable<string> {
    return this.cityService.activeBaseStoreCode$.pipe(distinctUntilChanged());
  }
}
